import { ReactNode, useContext, useEffect, useState } from 'react'
import { useParams, withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { Alert, Button, Form, ProgressBar, Table } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import { speak } from 'utils/util'
import { PatientViewModel } from 'view_models/patient_view_model'
import { FaCheckCircle, FaVolumeUp } from 'react-icons/fa'
import { ThemeContext } from 'contexts/theme_context'
import { useFetchSampleApi, useFetchSamplesApi, usePutConfirmedSampleApi, usePutRejectSampleApi } from 'api/sample'
import { useFetchProductItemsApi } from 'api/product_item'
import { ProductSearchForm } from 'entities/product'
import { useFetchDiseasesApi } from 'api/disease'
import { PastMedicalHistory, Sample, SampleInputForm, SampleSearchForm, SMOKING_STATUS_SELECT_ITEMS } from 'entities/sample'
import { useShowTransportationReportApi } from 'api/transportation_report'
import { InputField } from 'components/shared/input'
import { TransportationReportViewModel } from 'view_models/transportation_report_view_model'
import { CouponCode } from 'entities/coupon_code'

type Props = {
    history: H.History
}

const InspectionPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const params = useParams<{ id: string }>()
    const form = useForm<SampleInputForm>({})
    const confirmedApi = usePutConfirmedSampleApi()
    const rejectApi = usePutRejectSampleApi()
    const productSearchForm = useForm<ProductSearchForm>({ perPage: 100000 })
    const productItemsApi = useFetchProductItemsApi(productSearchForm)
    const diseasesApi = useFetchDiseasesApi()
    const sampleSearchForm = useForm<SampleSearchForm>({ inspectionStatus: 'INPUTTED' })
    const samplesApi = useFetchSamplesApi(sampleSearchForm)
    const showSampleApi = useFetchSampleApi()
    const showTransportationApi = useShowTransportationReportApi()
    const [speaking, setSpeaking] = useState<boolean>(false)
    const [pitch, setPitch] = useState<number>(1)
    const [rate, setRate] = useState<number>(1)

    useEffect(() => {
        productItemsApi.execute()
        diseasesApi.execute()
        showTransportationApi.execute(Number(params.id))
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(confirmedApi.loading)
        if (confirmedApi.isSuccess()) {
            sampleSearchForm.updateObject('requestForm', '')
            form.set({})
            showTransportationApi.execute(Number(params.id))
        }
    }, [confirmedApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(samplesApi.loading)
        if (samplesApi.isSuccess() && samplesApi.response.results.length > 0) {
            const sample: Sample = samplesApi.response.results[0]
            if (sample) showSampleApi.execute(sample.id!)
        } else {
            form.set({})
        }
    }, [samplesApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(showSampleApi.loading)
        if (showSampleApi.isSuccess()) {
            const sample = showSampleApi.response
            form.set({
                ...sample.patient,
                ...sample,
                pastMedicalHistories: sample.pastMedicalHistories,
                currentMedicalHistories: sample.currentMedicalHistories,
            })
        } else {
            form.set({})
        }
    }, [showSampleApi.loading])

    const handleClickNg = () => {
        globalState.showConfirm('差し戻しますか？', () => {
            rejectApi.execute(form.object.id!)
        })
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(rejectApi.loading)
        if (rejectApi.isSuccess()) {
            sampleSearchForm.updateObject('requestForm', '')
        }
    }, [rejectApi.loading])

    const showEmailCheck = () => {
        return form.object.patient?.email && form.object.checkedBirthday
    }
    const showTelCheck = () => {
        if (showEmailCheck()) {
            return form.object.patient?.tel && form.object.checkedEmail
        } else {
            return form.object.patient?.tel && form.object.checkedBirthday
        }
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(confirmedApi.loading)
        if (confirmedApi.isSuccess()) {
            form.set({})
        }
    }, [confirmedApi.loading])

    const vm = (): TransportationReportViewModel | undefined => {
        if (showTransportationApi.response) {
            return new TransportationReportViewModel(showTransportationApi.response)
        }
    }

    const isCompleted = (): boolean => {
        const ratio = vm()?.inspectedRatio()
        if (ratio) {
            return ratio >= 100
        } else {
            return false
        }
    }

    const getReadCoupons = (): string => {
        if (form.object.coupons?.length == 0) {
            return 'なし'
        }
        const strings: string[] = form.object.coupons?.map((coupon) => {
            let str = coupon.name
            if (form.object.couponCodes) {
                const couponCodes: CouponCode[] = form.object.couponCodes as CouponCode[]
                const couponCode = couponCodes.find((cc) => cc.couponId === coupon.id)
                if (couponCode) {
                    str = str + `(コード: ${couponCode.value})`
                }
            }
            return str
        }) as string[]
        return strings.join(' / ')
    }

    const checkInformationView = (
        readFlag: keyof SampleInputForm,
        checkedFlag: keyof SampleInputForm,
        label: string,
        value: string | ReactNode,
        readText?: string,
        onSuccess?: () => void
    ) => {
        return (
            <tr style={{ backgroundColor: form.getValue(checkedFlag) ? `${theme.green}11` : 'auto' }}>
                <td>
                    <FaCheckCircle color={form.getValue(checkedFlag) ? theme.green : theme.gray3} />
                </td>
                <td>{label}</td>
                <td>{value}</td>
                <td style={{ textAlign: 'right' }}>
                    <Flex alignItems="center" justifyContent="flex-end">
                        {form.getValue(readFlag) && !form.getValue(checkedFlag) && (
                            <>
                                <Button
                                    size="sm"
                                    variant="success"
                                    onClick={() => {
                                        form.updateObject(checkedFlag, true)
                                        if (onSuccess) {
                                            onSuccess()
                                        }
                                    }}
                                >
                                    OK
                                </Button>
                                <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => {
                                        handleClickNg()
                                    }}
                                >
                                    NG
                                </Button>
                            </>
                        )}
                        {!form.getValue(checkedFlag) && (
                            <Button
                                size="sm"
                                disabled={speaking}
                                onClick={() => {
                                    if (!speaking) {
                                        speak(
                                            readText ? readText : `${label}、${value}`,
                                            () => {
                                                setSpeaking(false)
                                                form.updateObject(readFlag, true)
                                            },
                                            pitch,
                                            rate
                                        )
                                        setSpeaking(true)
                                    }
                                }}
                            >
                                <FaVolumeUp />
                                <span style={{ marginLeft: 8 }}>読み上げ</span>
                            </Button>
                        )}
                    </Flex>
                </td>
            </tr>
        )
    }

    return (
        <>
            <ContentHeader
                headerTitle="検品 - 確認"
                left={
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                            props.history.goBack()
                        }}
                    >
                        戻る
                    </Button>
                }
            />
            <ContentBody>
                <div style={{ marginBottom: 20 }}>
                    <div>確認進捗</div>
                    <ProgressBar variant="success" now={vm()?.inspectedRatio()} label={`${vm()?.inspectedRatio()}%`}></ProgressBar>
                </div>
                {isCompleted() ? (
                    <Alert variant="success">この搬送報告分の検品は完了しています</Alert>
                ) : (
                    <div>
                        <InputField
                            onlyHankaku
                            form={form}
                            attr="preRequestFormBarcode"
                            required
                            label="検査依頼書のバーコード"
                            placeholder="スキャンしてEnterを押してください"
                            onPressEnter={() => {
                                sampleSearchForm.updateObject('requestForm', form.object.preRequestFormBarcode)
                            }}
                        />
                        {sampleSearchForm.object.requestForm && (
                            <div style={{ flexGrow: 1 }}>
                                {(() => {
                                    const vm = new PatientViewModel(form.object)
                                    if (!vm.object.id) {
                                        return <Alert variant="warning">そのバーコードの検体が見つかりません</Alert>
                                    }
                                    return (
                                        <div>
                                            <Flex alignItems="center" justifyContent="space-between">
                                                <h5>入力情報確認</h5>
                                                <Flex alignItems="center">
                                                    <Flex style={{ width: 200 }} alignItems="center">
                                                        <Form.Label style={{ width: 70 }}>声の高さ</Form.Label>
                                                        <Form.Range
                                                            style={{ width: 100 }}
                                                            onChange={(e) => {
                                                                setPitch(Number(e.target.value))
                                                            }}
                                                            min={0}
                                                            max={2}
                                                            step={0.5}
                                                        />
                                                    </Flex>
                                                    <Flex style={{ width: 200 }} alignItems="center">
                                                        <Form.Label style={{ width: 70 }}>読上速度</Form.Label>
                                                        <Form.Range
                                                            style={{ width: 100 }}
                                                            onChange={(e) => {
                                                                setRate(Number(e.target.value))
                                                            }}
                                                            min={0.5}
                                                            max={2}
                                                            step={0.1}
                                                        />
                                                    </Flex>
                                                </Flex>
                                            </Flex>

                                            <Table style={{ verticalAlign: 'middle', background: 'white' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 40 }}></th>
                                                        <th style={{ width: 170 }}>項目</th>
                                                        <th>入力値</th>
                                                        <th style={{ width: 350 }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {checkInformationView('readName', 'checkedName', '名前', vm.fullNameView(), vm.fullNameKana())}
                                                    {form.object.checkedName && checkInformationView('readGender', 'checkedGender', '性別', vm.gender())}
                                                    {form.object.checkedGender &&
                                                        checkInformationView('readBirthday', 'checkedBirthday', '生年月日', vm.birthdayText())}
                                                    {form.object.checkedBirthday &&
                                                        checkInformationView(
                                                            'readAddress',
                                                            'checkedAddress',
                                                            '住所',
                                                            vm.addressVm().wholeView(),
                                                            `住所、${vm.addressVm().readText()}`
                                                        )}
                                                    {form.object.checkedAddress &&
                                                        checkInformationView(
                                                            'readTel',
                                                            'checkedTel',
                                                            '電話番号',
                                                            vm.object.tel || 'なし',
                                                            `電話番号、${vm.object.tel || 'なし'}`
                                                        )}
                                                    {form.object.checkedTel &&
                                                        checkInformationView('readEmail', 'checkedEmail', 'メールアドレス', vm.object.email || 'なし')}
                                                    {form.object.checkedEmail &&
                                                        checkInformationView(
                                                            'readCurrentMedicalHistories',
                                                            'checkedCurrentMedicalHistories',
                                                            '現在治療中の病気',
                                                            `${
                                                                form.object.currentMedicalHistories
                                                                    ?.map((d) => {
                                                                        return `${d.part ? d.part : ''}${d.disease}`
                                                                    })
                                                                    .join(', ') || 'なし'
                                                            }`
                                                        )}
                                                    {form.object.checkedCurrentMedicalHistories &&
                                                        checkInformationView(
                                                            'readCurrentMedicalHistoryNote',
                                                            'checkedCurrentMedicalHistoryNote',
                                                            '備考(治療中の病気)',
                                                            form.object.currentMedicalHistoryNote,
                                                            `備考、${form.object.currentMedicalHistoryNote || 'なし'}`
                                                        )}
                                                    {form.object.checkedCurrentMedicalHistoryNote &&
                                                        checkInformationView(
                                                            'readPastMedicalHistories',
                                                            'checkedPastMedicalHistories',
                                                            '過去にかかった病気',
                                                            `${
                                                                form.object.pastMedicalHistories
                                                                    ?.map((d: PastMedicalHistory) => {
                                                                        return `${d.part ? d.part : ''}${d.disease}`
                                                                    })
                                                                    .join(', ') || 'なし'
                                                            }`
                                                        )}

                                                    {form.object.checkedPastMedicalHistories &&
                                                        checkInformationView(
                                                            'readPastMedicalHistoryNote',
                                                            'checkedPastMedicalHistoryNote',
                                                            '備考(過去の病気)',
                                                            form.object.pastMedicalHistoryNote,
                                                            `備考、${form.object.pastMedicalHistoryNote || 'なし'}`
                                                        )}
                                                    {form.object.checkedPastMedicalHistoryNote &&
                                                        checkInformationView(
                                                            'readFamilyHistories',
                                                            'checkedFamilyHistories',
                                                            '家族歴',
                                                            `${
                                                                form.object.familyHistories
                                                                    ?.filter((fh) => fh.relationship && fh.cancer)
                                                                    ?.map((fh) => {
                                                                        return `${fh.relationship} / ${fh.cancer}`
                                                                    })
                                                                    .join('、') || 'なし'
                                                            }`
                                                        )}

                                                    {form.object.checkedFamilyHistories &&
                                                        checkInformationView(
                                                            'readFamilyHistoryNote',
                                                            'checkedFamilyHistoryNote',
                                                            '備考(家族歴)',
                                                            form.object.familyHistoryNote,
                                                            `備考、${form.object.familyHistoryNote || 'なし'}`
                                                        )}
                                                    {form.object.checkedFamilyHistoryNote &&
                                                        checkInformationView(
                                                            'readSmoking',
                                                            'checkedSmoking',
                                                            'タバコ',
                                                            `${SMOKING_STATUS_SELECT_ITEMS.find((i) => i.value === form.object.smokingStatus)?.label} ${
                                                                form.object.smokingStatus === 'SMOKING'
                                                                    ? '(1日平均 ' + form.object.smokingPerDay + '本/約' + form.object.smokingYears + '年間)'
                                                                    : form.object.smokingStatus === 'BEFORE'
                                                                    ? '(' + form.object.smokingStartAge + '歳 から ' + form.object.smokingEndAge + '歳まで)'
                                                                    : ''
                                                            }`
                                                        )}

                                                    {form.object.checkedSmoking &&
                                                        checkInformationView(
                                                            'readSmokingNote',
                                                            'checkedSmokingNote',
                                                            '備考(タバコ)',
                                                            form.object.smokingNote,
                                                            `備考、${form.object.smokingNote || 'なし'}`
                                                        )}
                                                    {form.object.checkedSmokingNote &&
                                                        checkInformationView(
                                                            'readAlcohol',
                                                            'checkedAlcohol',
                                                            'アルコール',
                                                            `${
                                                                form.object.alcoholPerWeek
                                                                    ? form.object.alcoholPerWeek > 0
                                                                        ? `飲む (週${form.object.alcoholPerWeek}日)`
                                                                        : '飲む（週?日）'
                                                                    : '飲まない'
                                                            }`
                                                        )}

                                                    {form.object.checkedAlcohol &&
                                                        checkInformationView(
                                                            'readAlcoholNote',
                                                            'checkedAlcoholNote',
                                                            '備考(アルコール)',
                                                            form.object.alcoholNote,
                                                            `備考、${form.object.alcoholNote || 'なし'}`
                                                        )}
                                                    {form.object.checkedAlcoholNote &&
                                                        checkInformationView(
                                                            'readPregnancy',
                                                            'checkedPregnancy',
                                                            '妊娠中',
                                                            form.object.pregnancy ? 'はい' : 'いいえ'
                                                        )}
                                                    {form.object.checkedPregnancy &&
                                                        checkInformationView(
                                                            'readMenstruation',
                                                            'checkedMenstruation',
                                                            '生理中',
                                                            form.object.menstruation ? 'はい' : 'いいえ',
                                                            `せいりちゅう、${form.object.menstruation ? 'はい' : 'いいえ'}`
                                                        )}
                                                    {form.object.checkedMenstruation &&
                                                        checkInformationView(
                                                            'readDoctor',
                                                            'checkedDoctor',
                                                            '提出医',
                                                            `${form.object.doctorName}`,
                                                            `提出医、${form.object.doctorName || '不明'}`
                                                        )}
                                                    {form.object.checkedDoctor &&
                                                        checkInformationView(
                                                            'readKarte',
                                                            'checkedKarte',
                                                            'カルテ番号',
                                                            `${form.object.karteNumber}`,
                                                            `カルテ番号、${form.object.karteNumber?.split('').join('、')}`
                                                        )}
                                                    {form.object.checkedKarte &&
                                                        checkInformationView(
                                                            'readProduct',
                                                            'checkedProduct',
                                                            '製品',
                                                            `${productItemsApi.response.results
                                                                .filter((p) => form.object.productItems?.map((pi) => pi.id)?.includes(p.id!))
                                                                .map((p) => p.name)
                                                                .join(' / ')}`
                                                        )}
                                                    {form.object.checkedProduct &&
                                                        checkInformationView(
                                                            'readCollectionDate',
                                                            'checkedCollectionDate',
                                                            '採尿した日',
                                                            `${form.object.collectionDate}`,
                                                            `採尿した日、${form.object.collectionDate}`
                                                        )}
                                                    {form.object.checkedCollectionDate &&
                                                        checkInformationView('readCoupons', 'checkedCoupons', '優待適用', getReadCoupons())}
                                                    {form.object.checkedCoupons &&
                                                        checkInformationView(
                                                            'readFollowupStudyAgreement',
                                                            'checkedFollowupStudyAgreement',
                                                            'フォローアップ研究への同意',
                                                            form.object.followupStudyAgreement ? '同意します' : '同意しません'
                                                        )}
                                                </tbody>
                                            </Table>
                                            {form.object.checkedFollowupStudyAgreement && (
                                                <Button
                                                    variant="success"
                                                    style={{ width: '100%' }}
                                                    onClick={() => {
                                                        confirmedApi.execute(form.object.id!)
                                                    }}
                                                    disabled={!form.object.checkedSmoking}
                                                >
                                                    登録
                                                </Button>
                                            )}
                                        </div>
                                    )
                                })()}
                            </div>
                        )}
                    </div>
                )}
            </ContentBody>
        </>
    )
}

export default withRouter(InspectionPage)
